import { useCallback, useState } from 'react'
import {
  TableBody,
  TableCell,
  TableHead as MuiTableHead,
  TableRow,
  Table,
  TableContainer,
  Paper,
  Checkbox,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Link
} from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'
import { Link as RouterLink } from 'react-router-dom'

// components
import { CustomPagination } from '../../common/CustomPagination'
import { CustomTableCell } from '../../common/CustomTableCell'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { BankReconciliationListingProps, PaymentConciliationData } from '../../../interfaceTypes/props'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import VerifiedIcon from '@mui/icons-material/Verified'
import { BANK_RECONCILIATION_HEADERS, QUERY_PARAMS } from '../../../constants'
import { getProperPaymentMethod } from '../../../utils'
import { PaymentMethod } from '../../../../generated'
import { OverlayLoader } from '../../common/OverlayLoader'

const SELECT_ALL_WORKER_PATH = `${process.env.PUBLIC_URL || ''}/workers/reconcileAllPayments.worker.js`

/** Fixed column widths (percent) so header and body columns don't shift on scroll */
const RECONCILIATION_COLUMN_WIDTHS = [5, 10, 10, 10, 8, 8, 12, 27, 10] as const

const BankReconciliationListing = ({
  payments,
  loading,
  paymentIdsToReconcile,
  setPaymentIdsToReconcile
}: BankReconciliationListingProps) => {
  const [localLoading, setLocalLoading] = useState(false)
  const [searchParams] = useSearchParams()
  // Absent param = Line Item Detail ON, matching the checkbox / the parent screen / the Excel export.
  const isLineItemView = searchParams.get(QUERY_PARAMS.RECONCILIATION_LINE_ITEMS) !== 'false'

  const handleSelectAllPayments = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalLoading(true)

      if (event.target.checked) {
        requestAnimationFrame(() => {
          const worker = new Worker(SELECT_ALL_WORKER_PATH)
          worker.onmessage = (e: MessageEvent<{ allIds: string[] }>) => {
            worker.terminate()
            // Union with the existing selection (rather than replacing it) so selecting all under one Type
            // filter — e.g. Credit — is preserved when the user switches to Debit and selects all there too.
            setPaymentIdsToReconcile(prev => new Set<string>([...prev, ...e.data.allIds]))
            setLocalLoading(false)
          }
          worker.onerror = () => {
            worker.terminate()
            setLocalLoading(false)
          }
          worker.postMessage({
            payments: payments.map(p => ({
              id: p.id,
              reconciledAt: p.reconciledAt,
              paymentType: p.paymentType,
              amount: p.amount
            }))
          })
        })
      } else {
        requestAnimationFrame(() => {
          // Deselect only the rows visible under the current page/filter, keeping selections made elsewhere.
          const visibleIds = payments.map(p => p.id).filter((id): id is string => !!id)
          setPaymentIdsToReconcile(prev => {
            const next = new Set(prev)
            visibleIds.forEach(id => next.delete(id))
            return next
          })
          setLocalLoading(false)
        })
      }
    },
    [payments, setPaymentIdsToReconcile]
  )

  const handleSelectPayment = (payment: PaymentConciliationData, checked: boolean) => {
    const { id } = payment
    if (!id) return

    setPaymentIdsToReconcile(prev => {
      const next = new Set(prev)
      checked ? next.add(id) : next.delete(id)
      return next
    })
  }

  const handleSelectGroupedPayment = (payment: PaymentConciliationData, checked: boolean) => {
    const { paymentIds: groupPaymentIds } = payment
    const ids = groupPaymentIds ?? []
    if (ids.length === 0) return

    setPaymentIdsToReconcile(prev => {
      const next = new Set(prev)
      if (checked) {
        ids.forEach(id => next.add(id))
      } else {
        ids.forEach(id => next.delete(id))
      }
      return next
    })
  }

  // Header checkbox reflects the current page/filter only (selectable = unreconciled rows), so it stays
  // accurate even when the overall selection spans other filters that aren't currently visible.
  const selectableIds = payments.filter(p => p.id && !p.reconciledAt).map(p => p.id as string)
  const selectedVisibleCount = selectableIds.filter(id => paymentIdsToReconcile.has(id)).length
  const allSelected = selectableIds.length > 0 && selectedVisibleCount === selectableIds.length
  const someSelected = selectedVisibleCount > 0 && selectedVisibleCount < selectableIds.length

  const headerRow = (
    <TableRow hover>
      {BANK_RECONCILIATION_HEADERS.map((columnName, index) => (
        <TableCell
          key={index}
          component="th"
          align="left"
          sx={{ width: `${RECONCILIATION_COLUMN_WIDTHS[index]}%`, minWidth: 0, boxSizing: 'border-box' }}
        >
          {index === 0 && isLineItemView ? (
            <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAllPayments} />
          ) : (
            columnName
          )}
        </TableCell>
      ))}
    </TableRow>
  )

  const itemContent = (_index: number, payment: PaymentConciliationData) => {
    const {
      id,
      amount,
      date,
      officeId,
      officeName,
      paymentType,
      paymentMethod,
      referenceNumber,
      slipNumber,
      client,
      vendor,
      payable,
      reconciledAt,
      paymentIds
    } = payment
    const clientName = client ? `Client - ${client?.displayName?.trim() || ''} (#${client?.clientNumber})` : ''
    const vendorName = vendor ? `Vendor - ${vendor?.name} (#${vendor?.sortCode})` : ''
    // For AP (vendor) payments the check number lives on the linked payable; referenceNumber can hold the
    // voucher number (esp. migrated payments), so prefer the payable's check/ACH number for the Check No column.
    const checkNo = payable?.checkOrAchNumber || referenceNumber

    return (
      <>
        <TableCell>
          {reconciledAt ? (
            <Tooltip title={'Reconciled on ' + dayjs(reconciledAt).format('MM/DD/YYYY')}>
              <IconButton size="small">
                <VerifiedIcon color="success" />
              </IconButton>
            </Tooltip>
          ) : !!id ? (
            <Checkbox
              checked={paymentIdsToReconcile.has(id)}
              onChange={e => handleSelectPayment(payment, e.target.checked)}
            />
          ) : (
            <Tooltip
              title={
                !paymentIds?.length
                  ? 'No payments in this group. Switch to line item details to reconcile individually.'
                  : ''
              }
              arrow
              disableHoverListener={!!paymentIds?.length}
            >
              <span>
                <Checkbox
                  checked={!!paymentIds?.length && paymentIds.every(pid => paymentIdsToReconcile.has(pid))}
                  indeterminate={
                    !!paymentIds?.length &&
                    paymentIds.some(pid => paymentIdsToReconcile.has(pid)) &&
                    !paymentIds.every(pid => paymentIdsToReconcile.has(pid))
                  }
                  onChange={e => handleSelectGroupedPayment(payment, e.target.checked)}
                  disabled={!paymentIds?.length}
                />
              </span>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {payable?.voucherNo ? (
              // Linked to an Account Payable → link the check no. to the payable detail page (new tab so the
              // in-progress reconciliation isn't disturbed).
              <Link
                component={RouterLink}
                to={`/accounts/payable/${payable.voucherNo}`}
                target="_blank"
                rel="noopener"
                underline="hover"
                color="primary"
                variant="body1"
              >
                {checkNo || '--'}
              </Link>
            ) : (
              <Typography variant="body1" color="primary">
                {checkNo || '--'}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <CustomTableCell value={dayjs(date).format('MM/DD/YYYY')} />
        </TableCell>
        <CustomTableCell value={`$${amount.toFixed(2)}`} />
        <CustomTableCell value={paymentType || '--'} />
        <CustomTableCell value={slipNumber || '--'} />
        <CustomTableCell value={getProperPaymentMethod(paymentMethod as PaymentMethod)} />
        <CustomTableCell value={clientName || vendorName || '--'} preserveCase />
        <CustomTableCell value={officeName && officeId ? `${officeName} (#${officeId})` : '--'} />
      </>
    )
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper sx={{ width: '100%', overflow: 'hidden', '& td': { py: 0 } }}>
        <TableContainer sx={{ height: `100%` }}>
          {loading ? (
            <Table stickyHeader aria-label="sticky table">
              <MuiTableHead>{headerRow}</MuiTableHead>
              <TableSkeleton noOfRows={7} noOfCols={BANK_RECONCILIATION_HEADERS.length} />
            </Table>
          ) : payments.length === 0 ? (
            <Table stickyHeader aria-label="sticky table">
              <MuiTableHead>{headerRow}</MuiTableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={BANK_RECONCILIATION_HEADERS.length} align="center">
                    &nbsp;
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <TableVirtuoso<PaymentConciliationData>
              data={payments}
              fixedHeaderContent={() => headerRow}
              itemContent={itemContent}
              fixedItemHeight={52}
              style={{ height: '100%', minHeight: 460 }}
              components={{
                Table: ({ style, ...props }) => (
                  <Table
                    stickyHeader
                    aria-label="sticky table"
                    {...props}
                    style={{ ...style, width: '100%' }}
                    sx={{
                      tableLayout: 'fixed',
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                      '& .bank-recon-table-head th': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        backgroundColor: '#f5f5f5',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box'
                      }
                    }}
                  />
                ),
                TableHead: ({ children, style, ref: _ref, ...props }) => (
                  <thead
                    ref={_ref as React.RefObject<HTMLTableSectionElement>}
                    className="bank-recon-table-head"
                    {...props}
                    style={style}
                  >
                    {children}
                  </thead>
                ),
                TableRow: ({ children, style, ...props }) => (
                  <TableRow
                    component="tr"
                    hover
                    {...props}
                    style={style}
                    sx={{
                      '& > td': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                      }
                    }}
                  >
                    {children}
                  </TableRow>
                )
              }}
            />
          )}
        </TableContainer>
        <CustomPagination loading={loading || false} />
      </Paper>

      {localLoading && <OverlayLoader />}
    </Box>
  )
}

export default BankReconciliationListing
